import { useState, useRef, useCallback } from 'react';
import { Upload, FileText, AlertCircle, CheckCircle, X } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { parseCSV } from '@/lib/csv';
import { useContactsStore } from '@/stores/contactsStore';

interface ImportContactsModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

type Step = 'upload' | 'preview' | 'mapping' | 'importing' | 'result';

const CONTACT_FIELDS = [
  { value: '', label: '-- Skip --' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'type', label: 'Type' },
] as const;

const AUTO_MAP: Record<string, string> = {
  name: 'name',
  'full name': 'name',
  'contact name': 'name',
  email: 'email',
  'email address': 'email',
  phone: 'phone',
  'phone number': 'phone',
  telephone: 'phone',
  type: 'type',
  'contact type': 'type',
  category: 'type',
};

export function ImportContactsModal({ open, onClose, onComplete }: ImportContactsModalProps) {
  const create = useContactsStore((s) => s.create);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [progress, setProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);

  const reset = useCallback(() => {
    setStep('upload');
    setDragOver(false);
    setFileName('');
    setHeaders([]);
    setRows([]);
    setMapping({});
    setProgress(0);
    setImportTotal(0);
    setSuccessCount(0);
    setFailCount(0);
    setErrors([]);
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const processFile = (file: File) => {
    if (!file.name.endsWith('.csv')) {
      setErrors(['Please select a CSV file.']);
      return;
    }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCSV(text);
      if (parsed.headers.length === 0 || parsed.rows.length === 0) {
        setErrors(['The CSV file is empty or could not be parsed.']);
        return;
      }
      setHeaders(parsed.headers);
      setRows(parsed.rows);

      // Auto-map headers
      const autoMapping: Record<number, string> = {};
      parsed.headers.forEach((h, i) => {
        const normalized = h.toLowerCase().trim();
        if (AUTO_MAP[normalized]) {
          autoMapping[i] = AUTO_MAP[normalized];
        }
      });
      setMapping(autoMapping);
      setStep('preview');
    };
    reader.readAsText(file);
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) processFile(file);
    },
    []
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    // Reset file input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleMappingChange = (colIndex: number, fieldValue: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (fieldValue) {
        // Clear any other column mapped to the same field
        for (const key of Object.keys(next)) {
          if (next[Number(key)] === fieldValue) {
            delete next[Number(key)];
          }
        }
        next[colIndex] = fieldValue;
      } else {
        delete next[colIndex];
      }
      return next;
    });
  };

  const hasNameMapping = Object.values(mapping).includes('name');

  const handleImport = async () => {
    setStep('importing');
    setImportTotal(rows.length);
    let success = 0;
    let fail = 0;
    const importErrors: string[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const contact: Record<string, unknown> = {};

      for (const [colIdx, field] of Object.entries(mapping)) {
        const value = row[Number(colIdx)]?.trim();
        if (value) {
          contact[field] = value;
        }
      }

      // Name is required
      if (!contact.name) {
        fail++;
        importErrors.push(`Row ${i + 1}: Missing required field "name"`);
        setProgress(i + 1);
        setSuccessCount(success);
        setFailCount(fail);
        continue;
      }

      // Default type to 'lead' if not provided
      if (!contact.type) {
        contact.type = 'lead';
      }

      try {
        await create(contact);
        success++;
      } catch (err: unknown) {
        fail++;
        importErrors.push(`Row ${i + 1} (${contact.name}): ${(err as Error).message}`);
      }

      setProgress(i + 1);
      setSuccessCount(success);
      setFailCount(fail);
    }

    setErrors(importErrors);
    setStep('result');
    if (success > 0) {
      onComplete();
    }
  };

  const previewRows = rows.slice(0, 5);

  return (
    <Modal open={open} onClose={handleClose} title="Import Contacts" maxWidth="max-w-2xl">
      {/* Step: Upload */}
      {step === 'upload' && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-3 p-10 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
              dragOver
                ? 'border-nexus-500 bg-nexus-500/10'
                : 'border-surface-600 hover:border-surface-500 hover:bg-surface-700/30'
            }`}
          >
            <Upload className="h-8 w-8 text-surface-400" />
            <div className="text-center">
              <p className="text-sm font-medium text-surface-200">
                Drop a CSV file here, or click to browse
              </p>
              <p className="text-xs text-surface-500 mt-1">
                Supports .csv files with headers
              </p>
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={handleFileSelect}
          />
          {errors.length > 0 && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
              <p className="text-sm text-red-400">{errors[0]}</p>
            </div>
          )}
        </div>
      )}

      {/* Step: Preview */}
      {step === 'preview' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-nexus-400" />
            <p className="text-sm text-surface-300">
              <span className="font-medium text-surface-100">{fileName}</span>
              {' '}&mdash; {rows.length} row{rows.length !== 1 ? 's' : ''} found
            </p>
          </div>

          {/* Preview table */}
          <div className="overflow-x-auto border border-surface-700 rounded-lg">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-surface-700/50">
                  {headers.map((h, i) => (
                    <th key={i} className="px-3 py-2 text-left text-surface-400 font-medium whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row, ri) => (
                  <tr key={ri} className="border-t border-surface-700/50">
                    {headers.map((_, ci) => (
                      <td key={ci} className="px-3 py-1.5 text-surface-300 whitespace-nowrap max-w-[200px] truncate">
                        {row[ci] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {rows.length > 5 && (
            <p className="text-xs text-surface-500 text-center">
              Showing 5 of {rows.length} rows
            </p>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <Button onClick={() => setStep('mapping')}>
              Next: Map Fields
            </Button>
          </div>
        </div>
      )}

      {/* Step: Field Mapping */}
      {step === 'mapping' && (
        <div className="space-y-4">
          <p className="text-sm text-surface-400">
            Map each CSV column to a contact field. At minimum, the <span className="font-semibold text-surface-200">Name</span> field must be mapped.
          </p>

          <div className="space-y-3">
            {headers.map((header, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="text-sm text-surface-300 w-40 truncate shrink-0" title={header}>
                  {header}
                </span>
                <span className="text-surface-500 text-xs">&rarr;</span>
                <Select
                  options={CONTACT_FIELDS.map((f) => ({ value: f.value, label: f.label }))}
                  value={mapping[i] || ''}
                  onChange={(e) => handleMappingChange(i, e.target.value)}
                />
              </div>
            ))}
          </div>

          {!hasNameMapping && (
            <div className="flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-400">
                The "Name" field must be mapped to proceed.
              </p>
            </div>
          )}

          <div className="flex justify-between pt-2">
            <Button variant="secondary" onClick={() => setStep('preview')}>
              Back
            </Button>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleImport} disabled={!hasNameMapping}>
                Import {rows.length} Contact{rows.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Step: Importing */}
      {step === 'importing' && (
        <div className="space-y-4 py-4">
          <div className="text-center">
            <p className="text-sm font-medium text-surface-200">
              Importing contacts...
            </p>
            <p className="text-xs text-surface-500 mt-1">
              {progress} of {importTotal} processed
            </p>
          </div>

          {/* Progress bar */}
          <div className="w-full h-2 bg-surface-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-nexus-500 rounded-full transition-all duration-150"
              style={{ width: `${importTotal > 0 ? (progress / importTotal) * 100 : 0}%` }}
            />
          </div>

          <div className="flex justify-center gap-6 text-xs">
            <span className="text-emerald-400">{successCount} imported</span>
            {failCount > 0 && <span className="text-red-400">{failCount} failed</span>}
          </div>
        </div>
      )}

      {/* Step: Result */}
      {step === 'result' && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-surface-700/30 rounded-lg">
            <CheckCircle className="h-5 w-5 text-emerald-400 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-surface-100">
                Imported {successCount} contact{successCount !== 1 ? 's' : ''} successfully.
                {failCount > 0 && (
                  <span className="text-red-400"> {failCount} failed.</span>
                )}
              </p>
            </div>
          </div>

          {/* Error details */}
          {errors.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              <p className="text-xs font-medium text-surface-400">Error details:</p>
              {errors.map((err, i) => (
                <div key={i} className="flex items-start gap-2 px-3 py-1.5 bg-red-500/5 rounded text-xs text-red-400">
                  <X className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>{err}</span>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button onClick={handleClose}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
